import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const steps = [
  {
    number: '01',
    title: 'Create the Email Account in cPanel',
    description:
      'Before your form can send emails, you need an email account at your domain. This is the address that will receive contact form submissions.',
    icon: 'ri-mail-add-line',
    details: [
      'Log into your cPanel dashboard',
      'Find and click "Email Accounts" (under the Email section)',
      'Click "Create" to add a new email account',
      'Enter "info" as the username (creates info@yourdomain.com)',
      'Set a strong password and save it securely',
      'Choose a mailbox quota (e.g., 1GB) and click "Create"',
    ],
  },
  {
    number: '02',
    title: 'Upload the PHP Mail Handler',
    description:
      'The contact-form.php file handles receiving form data and sending it to your email. Place it in your public_html directory.',
    icon: 'ri-upload-cloud-line',
    details: [
      'In cPanel, open "File Manager"',
      'Navigate to your public_html directory',
      'Click "Upload" and select the contact-form.php file',
      'Alternatively, create a new file named contact-form.php and paste the code',
      'Ensure the file is in the same directory as your index.html',
    ],
  },
  {
    number: '03',
    title: 'Update the Form Endpoint',
    description:
      'Change your website\'s contact form to send data to your new PHP handler instead of the Readdy API.',
    icon: 'ri-link-m',
    details: [
      'Open your contact page source code (src/pages/contact/page.tsx)',
      'Find the fetch() call in the handleSubmit function',
      'Replace the Readdy API URL with your domain: https://yourdomain.com/contact-form.php',
      'Rebuild and re-upload your site files',
      'The form will now POST to your own server',
    ],
  },
  {
    number: '04',
    title: 'Test the Contact Form',
    description:
      'Submit a test message to verify emails are being delivered to your inbox.',
    icon: 'ri-send-plane-line',
    details: [
      'Visit your contact page on the live site',
      'Fill out the form with test data',
      'Click "Send Message"',
      'Check the info@sunstonesystem.com inbox (via webmail or your email client)',
      'If no email arrives within 5 minutes, check spam/junk folders',
    ],
  },
  {
    number: '05',
    title: 'Configure Webmail Access (Optional)',
    description:
      'Set up how you want to read emails sent to info@sunstonesystem.com.',
    icon: 'ri-mail-open-line',
    details: [
      'In cPanel, go to "Email Accounts"',
      'Click "Check Email" next to your info@ account',
      'Choose a webmail client (Roundcube, Horde, or SquirrelMail)',
      'Alternatively, configure in Gmail/Outlook using IMAP/POP3 settings',
      'cPanel provides auto-config files for most email clients',
    ],
  },
];

const troubleshooting = [
  {
    problem: 'Emails not arriving in inbox',
    solution:
      'Check spam/junk folders first. Verify the PHP mail() function is enabled on your server (contact your host if unsure). Some shared hosts require SMTP authentication instead of mail(). If so, you will need to modify the PHP script to use PHPMailer with SMTP credentials.',
  },
  {
    problem: 'Form shows "Failed to send email" error',
    solution:
      'This means the PHP mail() function failed. Check that your hosting provider allows outbound email. Some hosts block mail() and require SMTP. Also verify the contact-form.php file has correct permissions (644) and is in the right directory.',
  },
  {
    problem: 'CORS error in browser console',
    solution:
      'Open contact-form.php and update the $allowedOrigins array to include your exact domain (with https://). If testing locally, add http://localhost:5173. The script must know which domains are allowed to send requests.',
  },
  {
    problem: 'Emails going to spam folder',
    solution:
      'Set up SPF and DKIM records for your domain in cPanel (under "Email Deliverability"). These DNS records authenticate your domain and improve email reputation. Also ensure your hosting IP is not blacklisted.',
  },
  {
    problem: 'Want to use SMTP instead of mail()',
    solution:
      'If your host blocks mail(), use PHPMailer with SMTP. In cPanel, find your SMTP settings under "Email Accounts" → "Connect Devices". Update the PHP script to use PHPMailer with your SMTP host, port, username, and password.',
  },
];

const phpCode = `<?php
/**
 * SunStone System - Contact Form Mail Handler
 * Place this file in your cPanel public_html directory.
 */

// CORS headers
header("Access-Control-Allow-Origin: *");
header("Access-Control-Allow-Methods: POST, OPTIONS");
header("Access-Control-Allow-Headers: Content-Type");
header("Content-Type: application/json");

if ($_SERVER['REQUEST_METHOD'] === 'OPTIONS') {
    http_response_code(200);
    exit;
}

if ($_SERVER['REQUEST_METHOD'] !== 'POST') {
    http_response_code(405);
    echo json_encode(['success' => false, 'error' => 'Method not allowed']);
    exit;
}

// Get form data
$data = $_POST;
$name = isset($data['name']) ? trim($data['name']) : '';
$email = isset($data['email']) ? trim($data['email']) : '';
$message = isset($data['message']) ? trim($data['message']) : '';

if (empty($name) || empty($email) || empty($message)) {
    http_response_code(400);
    echo json_encode(['success' => false, 'error' => 'Required fields missing']);
    exit;
}

if (!filter_var($email, FILTER_VALIDATE_EMAIL)) {
    http_response_code(400);
    echo json_encode(['success' => false, 'error' => 'Invalid email']);
    exit;
}

// Sanitize
$name = htmlspecialchars($name, ENT_QUOTES, 'UTF-8');
$email = htmlspecialchars($email, ENT_QUOTES, 'UTF-8');
$company = isset($data['company']) ? htmlspecialchars(trim($data['company']), ENT_QUOTES, 'UTF-8') : '';
$phone = isset($data['phone']) ? htmlspecialchars(trim($data['phone']), ENT_QUOTES, 'UTF-8') : '';
$service = isset($data['service']) ? htmlspecialchars(trim($data['service']), ENT_QUOTES, 'UTF-8') : '';
$message = htmlspecialchars($message, ENT_QUOTES, 'UTF-8');

// Build email
$to = 'info@sunstonesystem.com';
$subject = "New Contact Form Submission from $name";
$body = "Name: $name\\nEmail: $email\\n";
if ($company) $body .= "Company: $company\\n";
if ($phone) $body .= "Phone: $phone\\n";
if ($service) $body .= "Service: $service\\n";
$body .= "\\nMessage:\\n$message\\n";
$body .= "\\n---\\nIP: " . $_SERVER['REMOTE_ADDR'] . " | Date: " . date('Y-m-d H:i:s');

$headers = "From: $name <$email>\\r\\nReply-To: $email\\r\\n";

$mailSent = mail($to, $subject, $body, $headers);

if ($mailSent) {
    echo json_encode(['success' => true, 'message' => 'Message sent successfully.']);
} else {
    http_response_code(500);
    echo json_encode(['success' => false, 'error' => 'Failed to send email.']);
}`;

export default function EmailSetup() {
  const [copied, setCopied] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const copyPhp = () => {
    navigator.clipboard.writeText(phpCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-white border-b border-black/[0.06]">
        <div className="mx-auto px-6 lg:px-10 xl:px-16 text-center">
          <span className="eyebrow">Email Configuration</span>
          <h1 className="text-3xl lg:text-4xl font-extrabold text-page-ink mb-4 tracking-[-0.03em]">
            Set Up Contact Form Email
          </h1>
          <p className="text-page-muted text-base max-w-2xl mx-auto leading-relaxed font-light">
            Configure your cPanel hosting to receive contact form submissions directly at info@sunstonesystem.com. No third-party services needed.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-10 mx-auto px-6 lg:px-10 xl:px-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-black/[0.08] rounded-2xl p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#c97a2b]/10 border border-[#c97a2b]/20 rounded-lg">
                <i className="ri-mail-settings-line text-[#c97a2b] text-lg" />
              </div>
              <h2 className="text-xl font-bold text-page-ink">How It Works</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { step: '1', title: 'Visitor Fills Form', desc: 'User submits message on your contact page' },
                { step: '2', title: 'PHP Handler Receives', desc: 'contact-form.php processes the submission' },
                { step: '3', title: 'Email Delivered', desc: 'Message arrives at info@sunstonesystem.com' },
              ].map((item) => (
                <div key={item.step} className="p-5 bg-page rounded-xl border border-black/[0.06] text-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#c97a2b]/10 border border-[#c97a2b]/20 rounded-full mx-auto mb-3">
                    <span className="text-[#c97a2b] text-xs font-bold">{item.step}</span>
                  </div>
                  <h3 className="text-page-ink font-semibold text-sm mb-1">{item.title}</h3>
                  <p className="text-page-muted text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-10 mx-auto px-6 lg:px-10 xl:px-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-extrabold text-page-ink mb-8 tracking-[-0.02em] text-center">
            Setup Steps
          </h2>
          <div className="space-y-6">
            {steps.map((step) => (
              <div
                key={step.number}
                className="bg-white border border-black/[0.08] rounded-2xl p-8 lg:p-10 hover:border-[#c97a2b]/20 transition-colors"
              >
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-[#c97a2b]/10 border border-[#c97a2b]/20 rounded-xl">
                    <i className={`${step.icon} text-[#c97a2b] text-xl`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-[#c97a2b] text-xs font-bold tracking-wider">
                        STEP {step.number}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-page-ink mb-2">{step.title}</h3>
                    <p className="text-page-muted text-sm leading-relaxed mb-4">{step.description}</p>
                    <ul className="space-y-2">
                      {step.details.map((detail, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-[#c97a2b] text-xs" />
                          </div>
                          <span className="text-page-muted text-sm leading-relaxed">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHP Code */}
      <section className="py-10 mx-auto px-6 lg:px-10 xl:px-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-[#c97a2b]/20 rounded-2xl p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center bg-[#c97a2b]/10 border border-[#c97a2b]/20 rounded-lg">
                <i className="ri-code-box-line text-[#c97a2b] text-lg" />
              </div>
              <h2 className="text-xl font-bold text-page-ink">contact-form.php</h2>
            </div>
            <p className="text-page-muted text-sm leading-relaxed mb-6 ml-[52px]">
              This PHP script receives form submissions and forwards them via email. The file is already included in your project root as <code className="text-[#c97a2b] bg-[#c97a2b]/10 px-1.5 py-0.5 rounded text-xs">contact-form.php</code>. Just upload it to cPanel.
            </p>

            <div className="relative bg-[#0f0f0f] border border-white/[0.1] rounded-xl p-5 overflow-x-auto">
              <button
                onClick={copyPhp}
                className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-[#c97a2b] text-white text-xs font-semibold rounded-md hover:bg-[#b06a22] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-sm`} />
                {copied ? 'Copied!' : 'Copy Code'}
              </button>
              <pre className="text-xs text-white/70 font-mono leading-relaxed whitespace-pre-wrap pr-24">
                {phpCode}
              </pre>
            </div>

            <div className="mt-5 flex items-start gap-3 p-4 bg-[#c97a2b]/5 border border-[#c97a2b]/15 rounded-xl">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-[#c97a2b] text-sm" />
              </div>
              <p className="text-page-muted text-sm leading-relaxed">
                <strong className="text-page-ink">Note:</strong> The full version (with CORS domain restrictions) is in the <code className="text-[#c97a2b] bg-[#c97a2b]/10 px-1 py-0.5 rounded text-xs">contact-form.php</code> file at your project root. Use that one for production — it only accepts requests from your domain for security.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Update Frontend Code */}
      <section className="py-10 mx-auto px-6 lg:px-10 xl:px-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-black/[0.08] rounded-2xl p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#c97a2b]/10 border border-[#c97a2b]/20 rounded-lg">
                <i className="ri-edit-line text-[#c97a2b] text-lg" />
              </div>
              <h2 className="text-xl font-bold text-page-ink">Update Your Contact Form</h2>
            </div>
            <p className="text-page-muted text-sm leading-relaxed mb-6">
              After migrating to cPanel, change the form submission URL in your React code from the Readdy API to your own PHP handler:
            </p>
            <div className="bg-[#0f0f0f] border border-white/[0.1] rounded-xl p-5 font-mono text-xs text-white/60 leading-relaxed space-y-2">
              <div className="text-white/30">// OLD (Readdy API) — remove this:</div>
              <div className="text-red-400/80">await fetch('https://readdy.ai/api/form/d7mtq4fu2vahpmebfcgg', ...)</div>
              <div className="text-white/30 mt-3">// NEW (Your cPanel) — use this:</div>
              <div className="text-green-400/80">await fetch('https://yourdomain.com/contact-form.php', ...)</div>
            </div>
            <div className="mt-5 p-4 bg-page rounded-xl border border-black/[0.06]">
              <p className="text-page-muted text-sm leading-relaxed">
                Replace <code className="text-[#c97a2b] bg-[#c97a2b]/10 px-1 py-0.5 rounded text-xs">yourdomain.com</code> with your actual domain. The form data format stays the same — name, email, company, phone, service, and message fields are all sent as URL-encoded form data.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Troubleshooting */}
      <section className="py-10 mx-auto px-6 lg:px-10 xl:px-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-extrabold text-page-ink mb-8 tracking-[-0.02em] text-center">
            Troubleshooting
          </h2>
          <div className="space-y-3">
            {troubleshooting.map((item, index) => (
              <div
                key={index}
                className="bg-white border border-black/[0.08] rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-red-500/10 border border-red-500/20 rounded-lg flex-shrink-0">
                      <i className="ri-error-warning-line text-red-400 text-sm" />
                    </div>
                    <span className="text-page-ink font-semibold text-sm">{item.problem}</span>
                  </div>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i
                      className={`ri-arrow-down-s-line text-page-faint transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`}
                    />
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'}`}
                >
                  <div className="px-5 pb-5 pl-16">
                    <div className="flex items-start gap-2.5">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-lightbulb-line text-[#c97a2b] text-xs" />
                      </div>
                      <p className="text-page-muted text-sm leading-relaxed">{item.solution}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Reference */}
      <section className="py-10 mx-auto px-6 lg:px-10 xl:px-16 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#c97a2b]/10 to-transparent border border-[#c97a2b]/20 rounded-2xl p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#c97a2b]/10 border border-[#c97a2b]/20 rounded-lg">
                <i className="ri-bookmark-line text-[#c97a2b] text-lg" />
              </div>
              <h2 className="text-xl font-bold text-page-ink">Quick Reference</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: 'Recipient Email', value: 'info@sunstonesystem.com' },
                { label: 'PHP File', value: 'contact-form.php' },
                { label: 'Upload Location', value: 'public_html/' },
                { label: 'Form Method', value: 'POST (application/x-www-form-urlencoded)' },
                { label: 'Required Fields', value: 'name, email, message' },
                { label: 'Optional Fields', value: 'company, phone, service' },
              ].map((ref) => (
                <div
                  key={ref.label}
                  className="flex items-center justify-between p-4 bg-page rounded-xl border border-black/[0.06]"
                >
                  <span className="text-page-faint text-xs font-medium">{ref.label}</span>
                  <span className="text-page-ink text-sm font-semibold text-right">{ref.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}